/**
 * API Service for SEO Automation Platform
 * Handles all backend API calls
 */
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1'
import websocketService from './websocketService'
import { updateActivity, clearSession } from '../utils/sessionManager'
import type { ObservanceItem } from '../components/observances/ObservancePicker'

// --- Website Builder Onboarding Types ---

export interface WbOnboardingFields {
  brand_name: string | null;
  business_name: string | null;
  business_type: string | null;
  primary_business: string | null;
  business_description: string | null;
  logo_url: string | null;
  logo_url_raw: string | null;
  primary_font: string | null;
  differentiators: string[];
  products: Array<{ id?: number; name: string; description?: string; priority?: boolean }>;
  services: Array<{ id?: number; name: string; description?: string; priority?: boolean }>;
  business_address: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  postal_code: string | null;
  business_phone: string | null;
  facebook_url: string | null;
  instagram_url: string | null;
  linkedin_url: string | null;
  twitter_url: string | null;
  youtube_url: string | null;
  customer_description: string | null;
  business_tagline: string | null;
  preferred_cta: string | null;
}

export interface WbOnboardingResponse {
  is_onboarding_completed: boolean;
  fields: WbOnboardingFields;
  locked_fields: Record<keyof WbOnboardingFields, boolean>;
}

export type WbOnboardingPayload = Partial<WbOnboardingFields>;


/** GET /profile/social-media/observances */
export interface ObservanceSettingsResponse {
  status: string
  /** False until Social Media Manager setup is finished. */
  setup_complete: boolean
  settings?: {
    observance_enabled: boolean
    selected_observances: string[]
    country: string
    region: string
  }
  max_selections?: number
  /** This cycle and the observance posts already in its calendar; null without a subscription. */
  current_cycle?: {
    start_date: string
    end_date: string
    observances: CurrentCycleObservance[]
  } | null
}

export interface CurrentCycleObservance {
  id: string
  /** From the observance events table; null when the observance isn't in it. */
  name: string | null
  /** The picker's selection key, e.g. "IN:Mahalaya"; null when not in the table. */
  key: string | null
  date: string
  platforms: string[]
  post_ids: string[]
}

export const NETWORK_ERROR_MESSAGE =
  "Can't reach the server. Check your internet connection and try again."

/**
 * Whether `err` is a failed fetch() itself (Chrome "Failed to fetch", Firefox
 * "NetworkError…", Safari "Load failed") — not any TypeError, e.g. from code
 * reading an unexpected response shape.
 */
export function isNetworkError(err: any): boolean {
  return err instanceof TypeError && /fetch|network|load failed/i.test(err.message || '')
}

/**
 * One readable message from any error body the backend sends: `detail` as a
 * string, as `{message}` (e.g. `{errorCode, message}`) or as a validation list;
 * `{message}` / `{error}` bodies; or nothing (a proxy's HTML error page).
 * Never "[object Object]" or raw JSON.
 */
export function errorMessageFromBody(body: any, status?: number): string {
  const detail = body?.detail
  let message = ''
  if (typeof detail === 'string') {
    message = detail
  } else if (Array.isArray(detail)) {
    message = detail
      .map((err: { loc?: string[]; msg?: string }) => err?.msg || `Invalid ${err?.loc?.slice(-1)[0] || 'field'}`)
      .join('; ')
  } else if (detail && typeof detail === 'object') {
    message = detail.message || detail.detail || detail.error || ''
  }
  if (!message && typeof body?.message === 'string') message = body.message
  if (!message && typeof body?.error === 'string') message = body.error
  if (!message) {
    message = status && status >= 500
      ? `Something went wrong on our side (error ${status}). Please try again in a minute.`
      : status
        ? `The request failed (error ${status}). Please try again.`
        : 'Something went wrong. Please try again.'
  }
  // Clean ANSI escape colour codes (e.g. from Python exception logs)
  return String(message).replace(/[\u001b\x1b]\[[0-9;]*[a-zA-Z]/g, '')
}

function getAuthHeaders() {
  const token = localStorage.getItem('access_token')
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  }
}

class ApiService {
  public baseUrl: string

  constructor() {
    this.baseUrl = API_BASE_URL
  }

  public async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'GET'
    })
  }

  public async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  public async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data)
    })
  }

  public async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data)
    })
  }

  public async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'DELETE'
    })
  }

  public async getBlob(endpoint: string, options: RequestInit = {}): Promise<Blob> {
    updateActivity(false);
    const url = `${this.baseUrl}${endpoint}`;
    const headers: Record<string, string> = {
      ...getAuthHeaders(),
      ...(options.headers as Record<string, string>)
    };
    const config: RequestInit = { ...options, headers };
    const response = await fetch(url, config);
    if (!response.ok) {
      if (response.status === 401) {
        clearSession(false);
        throw new Error('Session expired');
      }
      throw new Error(`Failed to fetch blob: ${response.status}`);
    }
    return response.blob();
  }

  /**
   * Generic request handler
   */
  public async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`

    // Touch activity on every API call made
    updateActivity(false)

    const headers: Record<string, string> = {
      ...getAuthHeaders(),
      ...(options.headers as Record<string, string>)
    }

    // If body is FormData, let the browser set the Content-Type with the boundary
    if (options.body instanceof FormData) {
      delete headers['Content-Type']
    }

    const config: RequestInit = {
      ...options,
      headers
    }

    try {
      // Only a failure of fetch() itself (offline, DNS, CORS, dropped connection)
      // is a network error — not a TypeError from code further down.
      const response = await fetch(url, config).catch((fetchError) => {
        console.error('API NETWORK ERROR:', fetchError)
        throw new Error(NETWORK_ERROR_MESSAGE)
      })
      if (!response.ok) {
        if (response.status === 401) {
          clearSession(false)
          throw new Error('Session expired')
        }
        if (response.status === 429) {
          const errorData = await response.json().catch(() => ({}))
          const retryAfter = response.headers.get('Retry-After')
          throw Object.assign(
            new Error(`Too many requests right now. ${retryAfter ? `Try again in ${retryAfter}s.` : 'Try again in a few minutes.'}`),
            { response: { data: errorData, status: 429 }, rateLimited: true },
          )
        }
        const errorData = await response.json().catch(() => ({}))

        // An Error (so `instanceof Error` checks work) carrying the response,
        // as callers read err.message and err.response.data.
        throw Object.assign(new Error(errorMessageFromBody(errorData, response.status)), {
          response: {
            data: errorData,
            status: response.status
          }
        })
      }

      return await response.json()
    } catch (error: any) {
      console.error('API REQUEST ERROR:', error)

      throw error
    }
  }

  /**
   * Page 1: Submit Business Information
   */
  async submitBusinessInfo(data: {
    full_name: string
    email: string
    username: string
    business_name: string
    website_url: string
    business_description: string
    user_id?: string
  }) {
    return this.request('/onboarding/page1/business-info', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  /**
   * Helper: Scrape Business Description
   */
  async scrapeUrl(url: string) {
    return this.request<{
      status: string
      description?: string
      error?: string
      url: string
    }>(`/onboarding/scrape-url?url=${encodeURIComponent(url)}`, {
      method: 'POST',
    })
  }

  /**
   * Helper: Validate URL
   */
  async validateUrl(data: { url: string }) {
    return this.request<{
      status: string
      reachable?: boolean
      message?: string
    }>('/onboarding/validate-url', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  /**
   * OAuth: Get Authorization URL
   */
  async getOAuthUrl(userId?: string) {
    const params = userId ? `?user_id=${encodeURIComponent(userId)}` : ''
    return this.request(`/oauth/url${params}`, {
      method: 'GET'
    })
  }

  /**
   * SMM OAuth: Get Authorization URL for specific channel
   */
  async getSMMChannelOAuthUrl(platform: string, userId: string) {
    const params = new URLSearchParams({
      platform: platform,
      user_id: userId
    }).toString()
    return this.request<any>(`/smm/onboarding/oauth/url?${params}`, {
      method: 'GET'
    })
  }

  /**
   * OAuth: Exchange Code for Tokens
   */
  async exchangeOAuthCode(code: string, userId: string) {
    return this.request(`/oauth/callback?code=${code}&user_id=${userId}`, {
      method: 'GET'
    })
  }

  /**
   * OAuth: Check Status
   */
  async checkOAuthStatus(userId: string) {
    return this.request(`/oauth/status/${userId}`, {
      method: 'GET'
    })
  }

  /**
   * OAuth: Refresh Access Token
   */
  async refreshAccessToken(userId: string) {
    return this.request(`/oauth/refresh/${userId}`, {
      method: 'POST'
    })
  }

  /**
   * Page 2: Validate GSC/GA4 Connections
   */
  async validateConnections(data: {
    user_id: string
    access_token: string
    target_url: string
    validate_gsc?: boolean
    validate_ga4?: boolean
    ga4_property_id?: string
  }) {
    return this.request('/onboarding/page2/validate-connections', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  /**
   * @deprecated Use validateConnections instead
   * Page 2: Submit GSC Connection (Old Method)
   */
  async submitGSCConnection(data: { user_id: string; gsc_connected: boolean }) {
    return this.request('/onboarding/page2/gsc', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  /**
   * Page 3: Submit Audience Information
   */
  async submitAudienceInfo(data: {
    user_id: string
    location_scope: string
    selected_locations: string[]
    customer_description: string
    search_intent: string[]
  }) {
    return this.request('/onboarding/page3/audience-info', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  /**
   * Scrape the brand's website to infer audience demographics (age, gender, location, interests).
   */
  async scrapeAudienceDemographics(websiteUrl: string): Promise<{
    age_range?: string
    gender?: string
    location?: string
    interests?: string
  }> {
    return this.request(
      `/demographics/scrape?website_url=${encodeURIComponent(websiteUrl)}`
    )
  }

  /**
   * Page 4: Submit Business Portfolio
   */
  async submitPortfolio(data: {
    user_id: string
    products: Array<{ product_name: string; product_url: string | null }>
    services: Array<{ service_name: string; service_url: string | null }>
    differentiators: string[]
  }) {
    return this.request('/onboarding/page4/portfolio', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  /**
   * Page 5: Submit SEO Goals
   */
  async submitSEOGoals(data: { user_id: string; seo_goals: string[] }) {
    return this.request('/onboarding/page5/seo-goals', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  /**
   * Page 6: Generate Keywords
   */
  async generateKeywords(user_id: string) {
    return this.request(`/onboarding/page6/keywords`, {
      method: 'POST',
      body: JSON.stringify({ user_id })
    })
  }

  /**
   * Page 6 Step 1: Submit Keywords and Get Suggested Competitors
   * Submits selected + custom keywords, triggers competitor analysis via Google CSE
   */
  async submitKeywordsAndGetCompetitors(data: {
    user_id: string
    selected_keywords: string[]
    custom_keywords: string[]
  }) {
    return this.request('/onboarding/submit-keywords', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  /**
   * Page 6 Step 2: Submit Final Competitor Selection
   * Submits user's selected competitors from suggestions + manual additions
   */
  async submitFinalCompetitors(data: {
    user_id: string
    selected_competitors: string[]
    manual_competitors: Array<{ website: string; name?: string }>
  }) {
    return this.request('/onboarding/submit-competitors', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  /**
   * Page 7: Upload Brand Logo
   */
  async uploadBrandLogo(userId: string, file: File) {
    const formData = new FormData()
    formData.append('user_id', userId)
    formData.append('logo_file', file)

    return this.request<{ status: string; data: { logo_url: string } }>(
      '/onboarding/upload-logo',
      {
        method: 'POST',
        body: formData
      }
    )
  }

  /** Brand onboarding: save the background-removed logo next to the original. */
  async uploadBrandLogoTransparent(userId: string, file: File) {
    const formData = new FormData()
    formData.append('user_id', userId)
    formData.append('logo_file', file)
    return this.request<{ status: string; data: { logo_transparent_url: string } }>(
      '/onboarding/upload-logo-transparent',
      { method: 'POST', body: formData }
    )
  }

  /**
   * Brand onboarding: preview the logo with its background removed (nothing saved;
   * upload the result with uploadBrandLogo if the user keeps it).
   */
  async removeOnboardingLogoBackground(userId: string, file: File) {
    const formData = new FormData()
    formData.append('user_id', userId)
    formData.append('logo_file', file)

    return this.request<{ status: string; data: { image: string; method: string } }>(
      '/onboarding/logo/remove-background',
      {
        method: 'POST',
        body: formData
      }
    )
  }

  /**
   * Page 7: Submit Content Filter
   */
  async submitContentFilter(data: {
    user_id: string
    page_urls: {
      home: string
      product: string
      contact: string
      about: string
      blog: string
    }
    blog_authors: Array<{
      name: string
      profileUrl: string
      role?: string
      bio?: string
    }>
    primary_color?: string
    secondary_color?: string
    logo_url?: string
    primary_font?: string | null
    secondary_font?: string | null
    voice_tone?: string | null
  }) {
    return this.request('/onboarding/page7/content-filter', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  /**
   * Page 8: Submit Reporting Settings
   */
  async submitReporting(data: {
    user_id: string
    reporting_channels: string[]
    email_addresses: string[]
    report_frequency: string
  }) {
    return this.request('/onboarding/reporting', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  // ==================== Strategy / Keyword Planner API ====================

  /**
   * Strategy: Generate V2 Keyword Universe
   * POST /api/v1/v2/optimization/keyword-planner/generate
   */
  async initKeywordUniverse(
    user_id: string
  ): Promise<{ status: string; data: any }> {
    return this.request<{ status: string; data: any }>(
      '/v2/optimization/keyword-planner/generate',
      {
        method: 'POST',
        body: JSON.stringify({ user_id, force: false })
      }
    )
  }

  /**
   * Strategy: Fetch V2 Keyword Universe
   * GET /api/v1/v2/optimization/keyword-planner/{user_id}
   */
  async getKeywordUniverse(
    user_id: string
  ): Promise<{ status: string; data: any }> {
    return this.request<{ status: string; data: any }>(
      `/v2/optimization/keyword-planner/${user_id}`,
      {
        method: 'GET'
      }
    )
  }

  /**
   * Strategy: Lock / Finalize Keyword Selection
   * POST /api/v1/v2/optimization/keyword-planner/{user_id}/lock
   */
  async finalizeKeywordSelection(
    user_id: string,
    selected_keyword_ids: number[]
  ): Promise<{ status: string; data: { locked_until: string } }> {
    return this.request<{ status: string; data: { locked_until: string } }>(
      `/v2/optimization/keyword-planner/${user_id}/lock`,
      {
        method: 'POST',
        body: JSON.stringify({ selected_keyword_ids })
      }
    )
  }

  /**
   * Strategy: Get Keyword Provenance
   */
  async getKeywordProvenance(keyword_id: number) {
    return this.request<any>(
      `/v2/optimization/keyword-planner/keyword/${keyword_id}/provenance`,
      {
        method: 'GET'
      }
    )
  }

  /**
   * Get User Profile
   */
  async getUserProfile(user_id: string): Promise<any> {
    return this.request(`/profile/${user_id}`, {
      method: 'GET'
    })
  }

  /**
   * Update contact identity
   */
  async updateContactIdentity(user_id: string, payload: any) {
    return this.request(`/profile/contact-identity/${user_id}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    })
  }

  async sendPasswordChangeOtp(data: {
    user_id: string
    current_password: string
  }) {
    return this.request('/profile/password/send-otp', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async verifyPasswordOtpAndUpdate(data: {
    user_id: string
    otp: string
    new_password: string
  }) {
    return this.request('/profile/password/verify', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  /**
   * Get contact identity
   */
  async getContactIdentity(user_id: string) {
    return this.request<{ data: any }>(`/profile/contact-identity/${user_id}`, {
      method: 'GET'
    })
  }

  /**
   * Get full business profile
   */
  async getFullBusinessProfile(user_id: string) {
    return this.request<{ data: any }>(`/profile/business-profile/${user_id}`, {
      method: 'GET'
    })
  }

  /**
   * Get country codes
   */
  async getCountryCodes() {
    return this.request<{
      status: string
      message: string
      data: {
        country_codes: Array<{
          name: string
          code: string
          dialCode: string
        }>
      }
    }>('/profile/country-codes', {
      method: 'GET'
    })
  }

  /**
   * Validate tax number (format + checksum + GSTIN-state match)
   */
  async validateTax(data: { country_code: string; tax_number: string; state_name?: string }) {
    return this.request('/registration/validate-tax', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }
  /**
   * Update full business profile
   */
  async updateBusinessProfile(user_id: string, payload: any) {
    return this.request(`/profile/business-profile/${user_id}`, {
      method: 'PUT',
      body: JSON.stringify(payload)
    })
  }
  /**
   * Send OTP
   */
  async sendOtp(data: { type: string; value: string; user_id?: string }) {
    if (data.type === 'Phone') {
      let cleanPhone = (data.value || '').replace(/[^\d+]/g, '')
      if (cleanPhone && !cleanPhone.startsWith('+')) {
        cleanPhone = '+' + cleanPhone.replace(/\+/g, '')
      }
      return await this.request('/auth/send-phone-otp', {
        method: 'POST',
        body: JSON.stringify({
          phone_number: cleanPhone
        })
      })
    }

    return this.request('/onboarding/send-otp', {
      method: 'POST',
      body: JSON.stringify({
        email: data.value
      })
    })
  }

  /**
   * Verify OTP
   */
  async verifyOtp(data: {
    type: string
    otp: string
    value?: string
    user_id?: string
  }) {
    if (data.type === 'Phone') {
      let cleanPhone = (data.value || '').replace(/[^\d+]/g, '')
      if (cleanPhone && !cleanPhone.startsWith('+')) {
        cleanPhone = '+' + cleanPhone.replace(/\+/g, '')
      }
      return await this.request('/auth/verify-phone-otp', {
        method: 'POST',
        body: JSON.stringify({
          phone_number: cleanPhone,
          otp: data.otp
        })
      })
    }

    return this.request('/onboarding/verify-otp', {
      method: 'POST',
      body: JSON.stringify({
        email: data.value,
        otp: data.otp
      })
    })
  }
  /**
   * Update Business Profile Logo
   */
  // async updateBusinessProfileLogo(user_id: string, file: File) {
  //   const formData = new FormData();
  //   formData.append('file', file);

  //   return this.request(`/profile/business-profile/logo/${user_id}`, {
  //     method: 'PATCH',
  //     body: formData,
  //   });
  // }

  async updateBusinessProfileLogo(
    user_id: string,
    file: File
  ): Promise<{
    status?: string
    message?: string
    data?: {
      logo_url?: string
    }
  }> {
    const formData = new FormData()

    formData.append('logo_file', file)

    return this.request<{
      status?: string
      message?: string
      data?: {
        logo_url?: string
      }
    }>(`/profile/business-profile/logo/${user_id}`, {
      method: 'POST',
      body: formData
    })
  }

  /** Profile → Social Media: the observance settings from setup and this cycle's observance posts. */
  async getObservanceSettings() {
    return this.request<ObservanceSettingsResponse>('/profile/social-media/observances', {
      method: 'GET'
    })
  }

  /** This cycle's observances for a country — the read-only picker for the cycle already planned. */
  async getCurrentCycleObservances(country?: string) {
    const query = country ? `?country=${encodeURIComponent(country)}` : ''
    return this.request<{ status: string; message?: string | null; start_date?: string; end_date?: string; observances: ObservanceItem[] }>(
      `/profile/social-media/observances/current${query}`,
      { method: 'GET' }
    )
  }

  /** The user's selected observances between two dates, for the Content Calendar's days. */
  async getCalendarObservances(startDate: string, endDate: string) {
    const params = new URLSearchParams({ start_date: startDate, end_date: endDate })
    return this.request<{ status: string; message?: string | null; country: string; observances: ObservanceItem[] }>(
      `/profile/social-media/observances/calendar?${params.toString()}`,
      { method: 'GET' }
    )
  }

  /** Profile: save the background-removed logo next to the original. */
  async saveTransparentLogo(file: File) {
    const formData = new FormData()
    formData.append('logo_file', file)
    return this.request<{ status?: string; message?: string; data?: { logo_transparent_url?: string } }>(
      '/profile/logo/transparent',
      { method: 'POST', body: formData }
    )
  }

  /** Profile: remove the background-removed logo (posts fall back to the original). */
  async removeTransparentLogo() {
    return this.request<{ status: string }>('/profile/logo/transparent', { method: 'DELETE' })
  }

  /**
   * Profile: preview the brand logo with its background removed (nothing saved).
   * Sends `file` when given, otherwise the server uses the logo saved on the profile.
   */
  async removeLogoBackground(file?: File | null) {
    // An empty multipart body can't be parsed server-side, so send none at all without a file.
    let body: FormData | undefined
    if (file) {
      body = new FormData()
      body.append('logo_file', file)
    }

    return this.request<{ status: string; data: { image: string; method: string } }>(
      '/profile/logo/remove-background',
      {
        method: 'POST',
        body
      }
    )
  }

  // ==================== AS-IS State API ====================

  /**
   * AS-IS: Get Summary Data (Four Cards)
   */
  async getAsIsSummary(data: {
    user_id: string
    site_url: string
    tracked_keywords?: string[]
    competitors?: string[]
  }) {
    return this.request<any>('/asis/summary', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  /**
   * AS-IS: Get Parameters for a Tab
   */
  async getAsIsParameters(data: {
    user_id: string
    site_url: string
    tab?: string
    status_filter?: string
    priority_urls?: string[]
    tracked_keywords?: string[]
  }) {
    return this.request<any>('/asis/parameters', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  /**
   * AS-IS: Get Parameter Group Details
   */
  async getAsIsParameterDetails(
    user_id: string,
    tab: string,
    group_id: string
  ) {
    return this.request<any>(
      `/asis/parameters/${tab}/${group_id}?user_id=${user_id}`,
      {
        method: 'GET'
      }
    )
  }

  /**
   * AS-IS: Get Competitor Rankings
   */
  async getAsIsCompetitors(data: {
    user_id: string
    site_url: string
    competitors: string[]
  }) {
    return this.request<any>('/asis/competitors', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  /**
   * AS-IS: Refresh All Data
   */
  async refreshAsIsData(data: {
    user_id: string
    site_url: string
    priority_urls?: string[]
    tracked_keywords?: string[]
    competitors?: string[]
  }) {
    return this.request('/asis/refresh', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  // ==================== Governance As-Is API ====================

  /**
   * Governance: Get Performance Overview
   */
  async getGovernancePerformance(user_id: string) {
    return this.request(`/governance/asis/performance/${user_id}`, {
      method: 'GET'
    })
  }

  /**
   * Governance: Get Performance Categories
   */
  async getGovernanceCategories(user_id: string, category: string = 'onpage') {
    return this.request(
      `/governance/asis/categories/${user_id}?category=${category}`,
      {
        method: 'GET'
      }
    )
  }

  /**
   * Governance: Get Progress Timeline
   */
  async getGovernanceTimeline(user_id: string, limit: number = 10) {
    return this.request(`/governance/asis/timeline/${user_id}?limit=${limit}`, {
      method: 'GET'
    })
  }

  /**
   * Governance: Get quick stats for overview cards
   */
  async getGovernanceQuickStats(user_id: string) {
    return this.request(`/governance/asis/quick-stats/${user_id}`, {
      method: 'GET'
    })
  }

  /**
   * Governance: Capture Baseline
   */
  async captureBaseline(user_id: string) {
    return this.request(`/governance/asis/baseline/${user_id}`, {
      method: 'POST'
    })
  }

  /**
   * Governance: Refresh As-Is Data
   */
  async refreshGovernanceData(user_id: string) {
    return this.request(`/governance/asis/refresh/${user_id}`, {
      method: 'POST'
    })
  }

  /**
   * Governance AS-IS: Get baseline/current/delta parameters for a tab
   */
  async getGovernanceParameters(user_id: string, tab: string) {
    return this.request(
      `/asis/governance/parameters?user_id=${encodeURIComponent(
        user_id
      )}&tab=${tab}`,
      {
        method: 'GET'
      }
    )
  }

  /**
   * Governance AS-IS: Trigger re-scan for a tab to populate Current column
   */
  async refreshGovernanceTab(user_id: string, tab: string) {
    return this.request(
      `/asis/governance/refresh?user_id=${encodeURIComponent(
        user_id
      )}&tab=${tab}`,
      {
        method: 'POST'
      }
    )
  }

  // ==================== Governance - Action Plan API ====================

  /**
   * Governance: Get Action Plan Performance
   */
  async getActionPlanGovernancePerformance(user_id: string) {
    return this.request(`/governance/action-plan/performance/${user_id}`, {
      method: 'GET'
    })
  }

  /**
   * Governance: Get Action Plan Categories
   */
  async getActionPlanGovernanceCategories(user_id: string) {
    return this.request(`/governance/action-plan/categories/${user_id}`, {
      method: 'GET'
    })
  }

  /**
   * Governance: Get Action Plan Timeline
   */
  async getActionPlanGovernanceTimeline(user_id: string, limit: number = 10) {
    return this.request(
      `/governance/action-plan/timeline/${user_id}?limit=${limit}`,
      {
        method: 'GET'
      }
    )
  }

  /**
   * Governance: Capture Action Plan Baseline
   */
  async captureActionPlanBaseline(user_id: string) {
    return this.request(`/governance/action-plan/baseline/${user_id}`, {
      method: 'POST'
    })
  }

  /**
   * Governance: Capture Action Plan Snapshot
   */
  async captureActionPlanSnapshot(user_id: string) {
    return this.request(`/governance/action-plan/snapshot/${user_id}`, {
      method: 'POST'
    })
  }

  /**
   * Governance: Get Action Plan Parameter Groups Breakdown
   */
  async getActionPlanParameterGroups(user_id: string) {
    return this.request(`/governance/action-plan/parameter-groups/${user_id}`, {
      method: 'GET'
    })
  }

  /**
   * Governance: Get Action Plan Recent Activity
   */
  async getActionPlanRecentActivity(user_id: string, limit: number = 10) {
    return this.request(
      `/governance/action-plan/recent-activity/${user_id}?limit=${limit}`,
      {
        method: 'GET'
      }
    )
  }

  /**
   * Governance: Get Detailed Action Plan Overview
   */
  async getActionPlanDetailedOverview(user_id: string) {
    return this.request(
      `/governance/action-plan/detailed-overview/${user_id}`,
      {
        method: 'GET'
      }
    )
  }

  /**
   * Governance: Get Category-Specific Action Plan Overview
   */
  async getActionPlanCategoryOverview(
    user_id: string,
    category: 'onpage' | 'offpage' | 'technical'
  ) {
    return this.request(
      `/governance/action-plan/category/${user_id}/${category}`,
      {
        method: 'GET'
      }
    )
  }

  // ==================== Governance - Goals API ====================

  /**
   * Governance: Get Goals Performance
   */
  async getGovernanceGoalsPerformance(user_id: string) {
    return this.request(`/governance/goals/performance/${user_id}`, {
      method: 'GET'
    })
  }

  /**
   * Governance: Get Goals Breakdown
   */
  async getGovernanceGoalsBreakdown(user_id: string) {
    return this.request(`/governance/goals/breakdown/${user_id}`, {
      method: 'GET'
    })
  }

  /**
   * Governance: Get Goals Cycle Info
   */
  async getGovernanceGoalsCycle(user_id: string) {
    return this.request(`/governance/goals/cycle/${user_id}`, {
      method: 'GET'
    })
  }

  /**
   * Governance: Capture Goals Snapshot
   */
  async captureGovernanceGoalsSnapshot(user_id: string) {
    return this.request(`/governance/goals/snapshot/${user_id}`, {
      method: 'POST'
    })
  }

  // ==================== Channel Governance API ====================

  /**
   * Channel Governance: Get Dashboard
   */
  async getChannelGovernanceDashboard(
    channel: string,
    user_id: string,
    months: number = 6
  ): Promise<any> {
    return this.request<any>(
      `/content-manager/governance/${channel}/dashboard/${user_id}?months=${months}`,
      {
        method: 'GET'
      }
    )
  }

  /**
   * Channel Governance: Check Status
   */
  async checkChannelConnectedStatus(
    channel: string,
    user_id: string
  ): Promise<any> {
    return this.request<any>(
      `/content-manager/governance/${channel}/connected/${user_id}`,
      {
        method: 'GET'
      }
    )
  }

  // ==================== Channel Optimization API ====================

  /**
   * Channel Optimization: Get Dashboard
   */
  async getChannelOptimizationDashboard(
    channel: string,
    user_id: string
  ): Promise<any> {
    return this.request<any>(
      `/content-manager/optimization/${channel}/dashboard/${user_id}`,
      {
        method: 'GET'
      }
    )
  }

  // ==================== Predictive Recalibration API ====================

  // refresh: the page's Refresh button — stored result now, newer one fetched in the background
  async getPredictiveRecalibration(userId: string, refresh = false): Promise<any> {
    return this.request<any>(
      `/optimization/predictive-recalibration/${userId}${refresh ? '?refresh=true' : ''}`,
      { method: 'GET' }
    );
  }

  async sendToStrategy(data: {
    user_id: string;
    title: string;
    rationale: string;
    impact_level: string;
  }): Promise<any> {
    return this.request('/optimization/send-to-strategy', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ==================== Trending Topics API ====================

  /**
   * Get trending topics (cached, 30-day TTL)
   */
  async getTrendingTopics(
    user_id: string,
    channel: string = 'youtube'
  ): Promise<any> {
    return this.request<any>(
      `/content-manager/trending-topics/${user_id}?channel=${channel}`,
      {
        method: 'GET'
      }
    )
  }

  // ==================== Authentication API ====================

  /**
   * Login with username and password
   */
  async login(data: { username: string; password: string }): Promise<{
    status: string
    message: string
    user_id?: string
    token?: string
    username?: string
    full_name?: string
    onboarding_completed?: boolean
    logo_url?: string
  }> {
    return this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  /**
   * Check if current session token is valid
   */
  async checkSession(token: string): Promise<{
    status: string
    message: string
    user_id?: string
    username?: string
    email?: string
    onboarding_completed?: boolean
  }> {
    return this.request(`/auth/session?token=${encodeURIComponent(token)}`, {
      method: 'GET'
    })
  }



  /**
   * Logout (clear session on server)
   */
  async logout(): Promise<{ status: string; message: string }> {
    try {
      await this.request('/auth/logout', { method: 'POST' })
    } catch (err) {
      console.warn('Backend logout failed, proceeding with local cleanup:', err)
    } finally {
      websocketService.disconnect();
      clearSession(false);
    }

    return {
      status: 'success',
      message: 'Logged out successfully'
    }
  }

  /**
   * Blog: Get Blog History
   */
  async getBlogHistory(userId: string) {
    return this.request(`/blog/history/${userId}?_t=${Date.now()}`, {
      method: 'GET'
    })
  }

  /**
   * Blog: Get Blog Quota
   */
  async getBlogQuota(userId: string) {
    return this.request(`/blog/quota/${userId}`, {
      method: 'GET'
    })
  }

  // ==================== Agency API ====================

  /**
   * Agency Login
   */
  async agencyLogin(data: { email: string; password: string }): Promise<{
    status: string
    message: string
    user_id?: string
    token?: string
    username?: string
    full_name?: string
  }> {
    return this.request('/agency/login', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  /**
   * Register Agency
   */
  async registerAgency(data: {
    agency_name: string
    contact_person: string
    email: string
    phone?: string
    credentials?: string
    focus_areas?: string[]
  }) {
    return this.request('/agency/register', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  /**
   * Add Client Brands to Agency
   */
  async addAgencyClients(data: { agency_id: string; brand_names: string[] }) {
    return this.request('/agency/clients', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  /**
   * Get Agency Clients
   */
  async getAgencyClients(agencyId: string): Promise<{
    status: string
    message: string
    data?: {
      agency_id: string
      agency_name: string
      clients: Array<{
        id: number
        brand_name: string
        brand_user_id: string | null
        status: string
        created_at: string | null
      }>
      total_clients: number
    }
  }> {
    return this.request(`/agency/${agencyId}/clients`, {
      method: 'GET'
    })
  }

  /**
   * Lookup Brand by Exact Name
   */
  async lookupBrand(name: string): Promise<{
    status: string
    message: string
    data?: {
      found: boolean
      brand_user_id?: string
      brand_email?: string
      business_name?: string
    }
  }> {
    return this.request(
      `/agency/brand/lookup?brand_name=${encodeURIComponent(name)}`,
      {
        method: 'GET'
      }
    )
  }

  /**
   * Request Access to Existing Brand (sends OTP to brand email)
   */
  async requestBrandAccess(data: {
    agency_id: string
    brand_user_id: string
  }): Promise<{
    status: string
    message: string
    data?: { request_id?: number }
  }> {
    return this.request('/agency/brand/request-access', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  /**
   * Verify OTP and Permanently Link Brand to Agency
   */
  async verifyBrandAccess(data: {
    agency_id: string
    brand_user_id: string
    otp_code: string
  }): Promise<{
    status: string
    message: string
    data?: { brand_name?: string; brand_user_id?: string }
  }> {
    return this.request('/agency/brand/verify-access', {
      method: 'POST',
      body: JSON.stringify(data)
    })
  }

  async removeAgencyClient(agencyId: string, clientId: number): Promise<any> {
    return this.request(`/agency/${agencyId}/clients/${clientId}`, {
      method: 'DELETE'
    })
  }

  /**
   * Get all registered agencies for dropdown selection
   */
  async getAvailableAgencies(): Promise<{
    status: string
    message: string
    data?: {
      agencies: Array<{
        id: string
        name: string
        email?: string | null
        phone?: string | null
        website?: string | null
        contact_person: string
      }>
      total_agencies?: number
    }
  }> {
    return this.request('/agency', {
      method: 'GET'
    })
  }

  // ==================== Channel OAuth ====================

  /**
   * Get the OAuth authorization URL for a given channel.
   * Open the returned `authorization_url` in a popup window.
   */
  async getChannelOAuthUrl(
    channel: string,
    userId: string
  ): Promise<{
    status: string
    channel: string
    authorization_url: string
  }> {
    return this.request(
      `/channels/oauth/url?channel=${encodeURIComponent(
        channel
      )}&user_id=${encodeURIComponent(userId)}`,
      { method: 'GET' }
    )
  }

  /**
   * Get which channels the user has already connected.
   */
  async getChannelStatus(userId: string): Promise<{
    status: string
    user_id: string
    connected_channels: string[]
    channel_details: Record<
      string,
      { account_id: string | null; account_name: string | null }
    >
  }> {
    return this.request(`/channels/status/${encodeURIComponent(userId)}`, {
      method: 'GET'
    })
  }

  /**
   * Generate a Three-Pillar content calendar for the given user and month.
   * Returns a randomised list of post topics across all connected channels.
   */
  async generateContentCalendar(
    userId: string,
    targetChannel: string = 'youtube',
    monthYear?: string,
    themes?: any[],
    channels?: string[]
  ): Promise<{
    status: string
    user_id: string
    month_year: string
    total_posts: number
    posts: Array<{
      channel: string
      theme_title: string
      campaign_name: string
      post_topic: string
      hook: string
      description: string
      scheduled_date: string | null
    }>
  }> {
    const params = new URLSearchParams({ target_channel: targetChannel })
    if (monthYear) params.append('month_year', monthYear)
    return this.request(
      `/content-manager/generate-calendar?${params.toString()}`,
      {
        method: 'POST',
        body: JSON.stringify({ themes, channels })
      }
    )
  }

  /**
   * Fetch an already generated content calendar from the database.
   */
  async getContentCalendar(
    userId: string,
    targetChannel: string = 'youtube',
    monthYear?: string
  ): Promise<{
    status: string
    user_id: string
    month_year: string
    total_posts: number
    posts: Array<{
      channel: string
      theme_title: string
      campaign_name: string
      post_topic: string
      hook: string
      description: string
      scheduled_date: string | null
      created_at: string | null
      updated_at: string | null
      static_post_url: string | null
      status: string
    }>
  }> {
    const params = new URLSearchParams({
      user_id: userId,
      target_channel: targetChannel
    })
    if (monthYear) params.append('month_year', monthYear)
    return this.request(`/content-manager/calendar?${params.toString()}`, {
      method: 'GET'
    })
  }

  /**
   * FAQ: Get hierarchical page structure for FAQ selection
   */
  async getFAQStructure(userId: string) {
    return this.request(`/faq/structure/${userId}`, { method: 'GET' })
  }

  /**
   * FAQ: Get FAQ Quota
   */
  async getFAQQuota(userId: string) {
    return this.request(`/faq/quota/${userId}`, { method: 'GET' })
  }

  /**
   * FAQ: Generate FAQ for a single page
   */
  async generateFAQ(
    userId: string,
    page: { id: string; name: string; url?: string }
  ) {
    return this.request('/faq/generate', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, page })
    })
  }

  /**
   * FAQ: List all previously generated FAQs for a user
   */
  async listFAQs(userId: string) {
    return this.request(`/faq/history/${userId}`, { method: 'GET' })
  }

  /**
   * FAQ: Persist published/draft status for a generated FAQ
   */
  async updateFAQStatus(faqId: number, status: 'published' | 'draft', userId?: string) {
    const id = userId || localStorage.getItem('user_id')
    return this.request(`/faq/${faqId}/status?user_id=${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ status })
    })
  }

  /**
   * FAQ: Persist updated questions and answers for a generated FAQ
   */
  async updateFAQContent(faqId: number, faqs: Array<{ id?: number; question: string; answer: string }>, userId?: string) {
    const id = userId || localStorage.getItem('user_id')
    return this.request(`/faq/${faqId}/content?user_id=${id}`, {
      method: 'PUT',
      body: JSON.stringify({ faqs })
    })
  }


  async getTutorials(): Promise<{
    status: string
    message: string
    data: {
      tutorials: Array<{
        id: number
        title: string
        description: string
        videoUrl: string
        thumbnailUrl: string
        duration: string
        category: string
      }>
    }
  }> {
    return this.request<{
      status: string
      message: string
      data: {
        tutorials: Array<{
          id: number
          title: string
          description: string
          videoUrl: string
          thumbnailUrl: string
          duration: string
          category: string
        }>
      }
    }>('/profile/tutorials', {
      method: 'GET'
    })
  }

  // ==================== Content Manager — Implementation API ====================

  /**
   * Implementation: Fetch scheduled topics for a channel from the content_calender table.
   * Replaces the deleted fetchCalendarTopics helper in Implementation.tsx.
   */
  async getImplementationTopics(
    channel: string,
    userId: string,
    monthYear?: string
  ): Promise<import('../types/implementation').Topic[]> {
    const qs = monthYear ? `?month_year=${encodeURIComponent(monthYear)}` : ''
    const response = await this.request<{
      status: string
      data?:
      | { topics?: import('../types/implementation').Topic[] }
      | import('../types/implementation').Topic[]
      topics?: import('../types/implementation').Topic[]
    }>(
      `/content-manager/implementation/${encodeURIComponent(
        channel
      )}/calendar/${encodeURIComponent(userId)}${qs}`,
      { method: 'GET' }
    )
    if (Array.isArray(response.data)) return response.data
    return response.data?.topics ?? response.topics ?? []
  }

  /**
   * Implementation: Update the details of a scheduled post in content_calender.
   */
  async updateImplementationPost(
    userId: string,
    topicId: string,
    fields: Record<string, any>
  ): Promise<any> {
    return this.request<any>('/content-manager/implementation/update-post', {
      method: 'PATCH',
      body: JSON.stringify({
        user_id: userId,
        topic_id: topicId,
        ...fields,
      }),
    });
  }

  /**
   * Implementation: Delete a scheduled post from the content_calender table.
   */
  async deleteImplementationPost(
    channel: string,
    topicId: string,
    userId: string
  ): Promise<any> {
    return this.request<any>(
      `/content-manager/implementation/${encodeURIComponent(channel)}/delete-post/${encodeURIComponent(topicId)}?user_id=${encodeURIComponent(userId)}`,
      { method: 'DELETE' }
    );
  }

  /**
   * Implementation: Add a new draft post to the content_calender table.
   */
  async addImplementationPost(
    channel: string,
    data: {
      user_id: string;
      post_topic: string;
      hook?: string;
      description?: string;
      scheduled_date?: string | null;
      theme_title?: string;
      campaign_name?: string;
      month_year?: string;
    }
  ): Promise<any> {
    return this.request<any>(
      `/content-manager/implementation/${encodeURIComponent(channel)}/add-post`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      }
    );
  }

  /**
   * Implementation: Upload a manual video to S3 and schedule/publish it on YouTube.
   */
  async uploadManualVideo(
    userId: string,
    topicId: string,
    file: File,
    title: string,
    description: string,
    hashtags: string[],
    scheduledDate: string | null,
    useOptimized: boolean,
    publishNow: boolean = false
  ): Promise<any> {
    const formData = new FormData()
    formData.append('user_id', userId)
    formData.append('topic_id', topicId)
    formData.append('video_file', file)
    formData.append('title', title)
    formData.append('description', description)
    formData.append('hashtags', JSON.stringify(hashtags))
    if (scheduledDate) {
      formData.append('scheduled_date', scheduledDate)
    }
    formData.append('use_optimized_time', String(useOptimized))
    formData.append('publish_now', String(publishNow))

    return this.request<any>('/content-manager/implementation/youtube/upload-manual', {
      method: 'POST',
      body: formData,
    })
  }

  /**
   * Implementation: Generate a draft script or caption for a topic.
   * Replaces the deleted callGenerateScript helper in Implementation.tsx.
   */
  async generateImplementationDraft(
    channel: string,
    data: Record<string, unknown>
  ): Promise<import('../types/implementation').DraftResponse> {
    const response = await this.request<{
      status: string
      data: import('../types/implementation').DraftResponse
    }>(
      `/content-manager/implementation/${encodeURIComponent(
        channel
      )}/generate-draft`,
      { method: 'POST', body: JSON.stringify(data) }
    )
    return response.data
  }

  /**
   * Implementation: Generate a static post image and copy for a topic.
   */
  async generateImplementationStaticPost(
    channel: string,
    data: Record<string, unknown>
  ): Promise<import('../types/implementation').StaticPostResponse> {
    const response = await this.request<{
      status: string
      data: import('../types/implementation').StaticPostResponse
    }>(
      `/content-manager/implementation/${encodeURIComponent(
        channel
      )}/generate-static-post`,
      { method: 'POST', body: JSON.stringify(data) }
    )
    return response.data
  }

  /**
   * Implementation: Publish a generated static post or video to a channel.
   * Replaces the deleted callPublishStaticPost helper in Implementation.tsx.
   */
  async publishImplementationPost(
    channel: string,
    data: Record<string, unknown>
  ): Promise<import('../types/implementation').PublishResponse> {
    const response = await this.request<{
      status: string
      data: import('../types/implementation').PublishResponse
    }>(
      `/content-manager/implementation/${encodeURIComponent(channel)}/publish`,
      { method: 'POST', body: JSON.stringify(data) }
    )
    return response.data
  }

  /**
   * Implementation (YouTube): Poll the status of an AI video generation job.
   * Replaces the deleted fetchVideoStatus helper in Implementation.tsx.
   */
  async getYouTubeVideoStatus(
    jobId: string
  ): Promise<import('../types/implementation').VideoStatusResponse> {
    const response = await this.request<{
      status: string
      data: import('../types/implementation').VideoStatusResponse
    }>(
      `/content-manager/implementation/youtube/video-status/${encodeURIComponent(
        jobId
      )}`,
      { method: 'GET' }
    )
    return response.data
  }

  /**
   * Implementation (YouTube): Trigger Veo AI video generation for a topic.
   * Replaces the deleted callGenerateVideo helper in Implementation.tsx.
   */
  async generateYouTubeVideo(
    data: Record<string, unknown>
  ): Promise<import('../types/implementation').VideoGenerationResponse> {
    const response = await this.request<{
      status: string
      data: import('../types/implementation').VideoGenerationResponse
    }>(`/content-manager/implementation/youtube/generate-video`, {
      method: 'POST',
      body: JSON.stringify(data)
    })
    return response.data
  }

  /**
   * Implementation: Fetch campaign/theme mapping definitions for the UI.
   * Replaces the inline fetch in Implementation.tsx loadMapping().
   */
  async getThemeMapping(): Promise<
    import('../types/implementation').ThemeMapping
  > {
    const response = await this.request<{
      status: string
      data: import('../types/implementation').ThemeMapping
    }>(`/content-manager/strategy/theme-mapping`, { method: 'GET' })
    return response.data
  }

  // ==========================================
  // NEW WEBSITE BUILDER ENDPOINTS
  // ==========================================

  /**
   * Step 1: Send the toggled pages to create the project in the DB
   */
  async initializeProject(
    nodes: import('../mocks/websiteBuilder').SitemapNode[]
  ): Promise<any> {
    const response = await this.request<{ status: string; data: any }>(
      `/website-builder/initialize`,
      {
        method: 'POST',
        body: JSON.stringify({ nodes })
      }
    )
    return response.data
  }

  /**
   * Step 2: Trigger Gemini to write the text for all pages
   */
  async generateContent(): Promise<any> {
    const response = await this.request<{ status: string; data: any }>(
      `/website-builder/generate-content`,
      { method: 'POST' }
    )
    return response.data
  }

  /**
   * Step 3: Download the bundled ZIP file
   * Note: We use raw fetch here because we need to return a Blob (File), not JSON!
   */
  async exportWebsite(templateId: string): Promise<Blob> {
    const url = `${this.baseUrl}/website-builder/export`

    // We get the auth token from your existing helper function at the top of api.ts
    const headers = getAuthHeaders()

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ selected_template_id: templateId })
    })

    if (!response.ok) {
      throw new Error(`Export failed with status: ${response.status}`)
    }

    // Return the raw binary data for the ZIP file
    return await response.blob()
  }
  /** Fetch pages to load into the Content Editor */
  async getPages(): Promise<any> {
    const response = await this.request<{
      status: string
      data: { pages: any[]; globalContactInfo?: any }
    }>(`/website-builder/pages`, { method: 'GET' })
    return response.data
  }

  /** Save manual user edits from the Content Editor */
  async saveContent(pages: any[], globalContactInfo?: any): Promise<any> {
    const response = await this.request<{ status: string; data: any }>(
      `/website-builder/save-content`,
      {
        method: 'POST',
        body: JSON.stringify({ pages, globalContactInfo })
      }
    )
    return response.data
  }

  /** Website Builder: Get Business Profile */
  async getWebsiteProfile(userId: string) {
    return this.request<any>(`/profile/business-profile/${userId}`)
  }

  /** Website Builder: Check Session */
  async checkWebsiteSession() {
    return this.request<any>(`/website-builder/check-session`)
  }

  /** Website Builder: Get Project State (Draft / Downloaded / None) */
  async getWebsiteProjectState() {
    return this.request<any>(`/website-builder/project-state`)
  }

  /** Website Builder: Charge Upfront */
  async chargeWebsiteBuilder() {
    return this.request<any>(`/website-builder/charge`, {
      method: 'POST'
    })
  }

  /** Website Builder: Unlock Project for 5,000 credits */
  async unlockWebsiteBuilder() {
    return this.request<any>(`/website-builder/unlock`, {
      method: 'POST'
    })
  }

  /** Website Builder: Initialize Sitemap */
  async initializeWebsite(nodes: any[]) {
    return this.request<any>(`/website-builder/initialize`, {
      method: 'POST',
      body: JSON.stringify({ nodes })
    })
  }

  /** Website Builder: Generate Page Content */
  async generateWebsiteContent(pageId: string) {
    return this.request<any>(
      `/website-builder/generate-content?page_id=${pageId}`,
      {
        method: 'POST'
      }
    )
  }

  /** Website Builder: Start Fresh */
  async startFreshWebsite() {
    return this.request<any>(`/website-builder/start-fresh`, {
      method: 'POST'
    })
  }

  /** Website Builder: Regenerate Single Section */
  async regenerateWebsiteSection(
    pageSlug: string,
    sectionId: string,
    sectionType: string
  ) {
    return this.request<any>(`/website-builder/regenerate-section`, {
      method: 'POST',
      body: JSON.stringify({
        page_slug: pageSlug,
        section_id: sectionId,
        section_type: sectionType
      })
    })
  }

  /** Website Builder: Generate AI Image */
  async generateWebsiteImage(prompt: string) {
    return this.request<any>(`/website-builder/generate-image`, {
      method: 'POST',
      body: JSON.stringify({ prompt })
    })
  }

  /** Website Builder: Get Onboarding Status */
  async getWbOnboardingStatus(): Promise<{ status: string; message: string; data: { is_onboarding_completed: boolean; user_id: string } }> {
    return this.request<any>(`/website-builder/onboarding/status`, {
      method: 'GET'
    })
  }

  /** Website Builder: Get Merged Onboarding Profile & Locked Fields */
  async getWbOnboardingData(): Promise<{ status: string; message: string; data: WbOnboardingResponse }> {
    return this.request<any>(`/website-builder/onboarding`, {
      method: 'GET'
    })
  }

  /** Website Builder: Complete Onboarding (Split-write) */
  async completeWbOnboarding(payload: WbOnboardingPayload): Promise<{ status: string; message: string; data: any }> {
    return this.request<any>(`/website-builder/onboarding/complete`, {
      method: 'POST',
      body: JSON.stringify(payload)
    })
  }

  // ==================== Social Media Manager Image Generation ====================

  /** Generate Image (Domain Agnostic Pipeline) */
  async generateImage(postId: string, instructions?: string) {
    return this.request<any>(`/posts/${postId}/generate-image`, {
      method: 'POST',
      body: JSON.stringify({ instructions })
    });
  }

  /** Enhance Image / Carousel Slide using AI */
  async enhanceImage(postId: string, data?: { instructions?: string; slide_number?: number; input_image_url?: string }) {
    return this.request<any>(`/posts/${postId}/enhance-image`, {
      method: 'POST',
      body: JSON.stringify(data || {})
    });
  }

  /**
   * Get Image Generation Status. `status` is "PENDING" | "DONE" |
   * "DONE_WITH_FAULTS" | "FAILED" — DONE_WITH_FAULTS means image_options is
   * populated with the quality-gate's failed candidates and image_url/
   * carousel_slide_urls already hold a placeholder, not a hard failure.
   * Each image_options entry carries `angle` (older single-image recovery
   * mechanism) OR `issues`/`slide_number` (quality-gate faulty picker),
   * never both.
   */
  async getImageStatus(postId: string): Promise<{
    status: string;
    image_url?: string | null;
    carousel_slide_urls?: string[] | null;
    error_message?: string | null;
    image_options?: { url: string; angle?: string; issues?: string[]; slide_number?: number; selected?: boolean }[] | null;
    selected_image_index?: number | null;
  }> {
    return this.request<any>(`/posts/${postId}/image-status`, {
      method: 'GET'
    });
  }

  /** Select Image Variation from Recovery/Faulty-Picker Candidates */
  async selectImage(postId: string, selectedIndex: number): Promise<{
    status: string;
    image_url?: string | null;
    carousel_slide_urls?: string[] | null;
    error_message?: string | null;
    image_options?: { url: string; angle?: string; issues?: string[]; slide_number?: number; selected?: boolean }[] | null;
    selected_image_index?: number | null;
  }> {
    return this.request<any>(`/posts/${postId}/select-image`, {
      method: 'POST',
      body: JSON.stringify({ selected_index: selectedIndex })
    });
  }

  /** Static post logo placement: clearance score per corner + recommended corner */
  async getLogoCorners(postId: string): Promise<import('../types/logoPlacement').LogoCornersResponse> {
    return this.request<import('../types/logoPlacement').LogoCornersResponse>(
      `/static-post/${encodeURIComponent(postId)}/logo-corners`,
      { method: 'GET' }
    );
  }

  /** Static post logo placement: composite the logo at a corner ('none' removes it) in the given style (which logo, background, rounding) */
  async applyLogo(
    postId: string,
    position: import('../types/logoPlacement').LogoPosition,
    style?: import('../types/logoPlacement').LogoStyle
  ): Promise<import('../types/logoPlacement').ApplyLogoResponse> {
    return this.request<import('../types/logoPlacement').ApplyLogoResponse>(
      `/static-post/${encodeURIComponent(postId)}/apply-logo`,
      { method: 'POST', body: JSON.stringify({ position, ...(style ? { style } : {}) }) }
    );
  }

  /** Carousel logo placement: clearance score per corner for ONE slide + recommended corner. Each slide's corner is independent. */
  async getCarouselLogoCorners(postId: string, slideNumber: number): Promise<import('../types/logoPlacement').LogoCornersResponse> {
    return this.request<import('../types/logoPlacement').LogoCornersResponse>(
      `/content-generation/carousel/${encodeURIComponent(postId)}/logo-corners?slide_number=${slideNumber}`,
      { method: 'GET' }
    );
  }

  /** Carousel logo placement: composite the logo onto one slide — or, with allSlides, every slide — at a corner ('none' removes it). */
  async applyCarouselLogo(
    postId: string,
    position: import('../types/logoPlacement').LogoPosition,
    slideNumber: number,
    style?: import('../types/logoPlacement').LogoStyle,
    allSlides = false
  ): Promise<import('../types/logoPlacement').ApplyLogoResponse> {
    return this.request<import('../types/logoPlacement').ApplyLogoResponse>(
      `/content-generation/carousel/${encodeURIComponent(postId)}/apply-logo`,
      {
        method: 'POST',
        body: JSON.stringify({
          position,
          slide_number: slideNumber,
          ...(style ? { style } : {}),
          ...(allSlides ? { all_slides: true } : {}),
        }),
      }
    );
  }

  /** Website Builder: Extract Colors from Onboarding Profile */
  async extractWebsiteColors() {
    return this.request<any>(`/website-builder/extract-colors`, {
      method: 'POST'
    })
  }

  /** Website Builder: Export Project as ZIP */
  async exportWebsiteZip(
    templateId: string,
    htmlFiles: any,
    brandName?: string,
    brandLogo?: string | null
  ) {
    // We use fetch directly here because it's a binary blob, not JSON
    const token = localStorage.getItem('access_token')
    const response = await fetch(`${this.baseUrl}/website-builder/export`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify({
        selected_template_id: templateId,
        html_files: htmlFiles,
        brand_name: brandName,
        brand_logo: brandLogo
      })
    })

    if (!response.ok) throw new Error('Export failed')
    return response.blob()
  }

  /**
   * Secure address autocomplete via backend wrapper API
   */
  async getAddressAutocomplete(input: string) {
    return this.request<{
      status: string
      predictions: Array<{
        description: string
        place_id: string
      }>
    }>(
      `/profile/address/autocomplete?input_query=${encodeURIComponent(input)}`,
      {
        method: 'GET'
      }
    )
  }

  /**
   * Fetch place details by place_id
   */
  async getPlaceDetails (placeId: string) {
    return this.request<{
      status: string
      details: {
        street: string
        city: string
        state: string
        country: string
        postal_code: string
        formatted_address: string
      }
    }>(
      `/profile/address/details?place_id=${encodeURIComponent(placeId)}`,
      {
        method: 'GET'
      }
    )
  }

  // ==================== Onboarding & Agency Specific API Methods ====================

  async getAgencies() {
    return this.request<{ data: { agencies: any[] } }>('/agency', {
      method: 'GET'
    });
  }

  async checkUsername(data: { username: string }) {
    return this.request<{ message: string }>('/onboarding/check-username', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async checkEmail(data: { email: string }) {
    return this.request<{ success?: boolean; message: string }>('/onboarding/check-email', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }


  async getLatestTermsAndConditions() {
    return this.request<{ data: { details: string } }>(`/onboarding/get-latest-terms-and-conditions`, {
      method: 'GET'
    });
  }

  async acceptLatestTerms(data: { user_id: string; email: string }) {
    return this.request<any>('/onboarding/accept-latest-terms', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async saveFinalOnboardingData(data: any) {
    return this.request<any>('/onboarding/save-onboarding-data', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async getOnboardingData(companyName: string) {
    const url = `/onboarding/get-onboarding-data?company_name=${encodeURIComponent(companyName)}`;
    return this.request<any>(url, {
      method: 'GET'
    });
  }

  async deleteTempOnboardingData(companyName: string) {
    return this.request<any>(`/onboarding/delete-onboarding-data-temp?company_name=${encodeURIComponent(companyName)}`, {
      method: 'DELETE'
    });
  }

  async registerAgencyFromOnboarding(data: { agency_name: string; contact_person: string; email: string; phone: string }) {
    return this.request<{ agency_id: string }>('/agency/register', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async saveTempOnboardingData(data: any) {
    return this.request<any>('/onboarding/save-onboarding-data-temp', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  // ==================== Shared API ====================
  async uploadSharedFile(file: File): Promise<{ status?: string; message?: string; data?: { file_url?: string } }> {
    const formData = new FormData();
    formData.append('file', file);
    return this.request('/shared/upload', {
      method: 'POST',
      body: formData,
    });
  }

  async uploadTicketAttachment(file: File): Promise<{ success?: boolean; file_url?: string; filename?: string }> {
    const formData = new FormData();
    formData.append('file', file);
    return this.request('/tickets/upload', {
      method: 'POST',
      body: formData,
    });
  }

  // ==================== Customer Support Ticketing API ====================

  async createTicket(payload: any) {
    return this.request('/tickets/', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  async getTickets(params?: { skip?: number; limit?: number; status_filter?: string; priority_filter?: string }) {
    const query = new URLSearchParams();
    if (params?.skip !== undefined) query.append('skip', params.skip.toString());
    if (params?.limit !== undefined) query.append('limit', params.limit.toString());
    if (params?.status_filter) query.append('status', params.status_filter);
    if (params?.priority_filter) query.append('priority', params.priority_filter);

    const queryString = query.toString();
    const endpoint = queryString ? `/tickets/?${queryString}` : '/tickets/';
    return this.request(endpoint, {
      method: 'GET'
    });
  }

  async getTicketById(ticket_id: string | number) {
    return this.request(`/tickets/${ticket_id}`, {
      method: 'GET'
    });
  }

  async getTicketReplies(ticket_id: string | number) {
    return this.request(`/tickets/${ticket_id}/replies`, {
      method: 'GET'
    });
  }

  async addTicketReply(ticket_id: string | number, payload: any) {
    return this.request(`/tickets/${ticket_id}/reply`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  async closeTicket(ticket_id: string | number) {
    return this.request(`/tickets/${ticket_id}/close`, {
      method: 'POST'
    });
  }

  async submitFeedback(ticket_id: string | number, payload: any) {
    return this.request(`/tickets/${ticket_id}/feedback`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  // ==================== Ask Kiki API ====================

  async sendMessageToChatbot(user_id: string, query: string, signal?: AbortSignal) {
    return this.request('/chatbot/message', {
      method: 'POST',
      body: JSON.stringify({ message: query }),
      signal
    });
  }

  async sendMessageToGuestChatbot(session_id: string, query: string, signal?: AbortSignal) {
    return this.request('/chatbot/guest_message', {
      method: 'POST',
      body: JSON.stringify({ session_id, query }),
      signal
    });
  }
  // ==================== Static Post Generation API ====================

  /**
   * Dispatch static poster generation. The backend runs generation in a
   * background task: POST returns immediately with status="pending" and
   * the final output only arrives via GET /static-post/status/{campaign_id}.
   * Consumers should use useStaticPostGeneration / staticPostGeneration.ts.
   */
  async generateStaticPoster(data: Record<string, any>): Promise<any> {
    const response = await this.request<any>('/static-post/generate', {
      method: 'POST',
      body: JSON.stringify(data)
    });
    return response;
  }

  /**
   * Poll the background static post generation job for a campaign. Returns
   * pending while the worker runs, done once the output is ready, or error
   * (credit charge already auto-refunded by the backend).
   */
  async getStaticPostStatus(
    campaignId: string
  ): Promise<import('../types/staticPost').StaticPostGenerateResponse> {
    return this.request<import('../types/staticPost').StaticPostGenerateResponse>(
      `/static-post/status/${encodeURIComponent(campaignId)}`,
      { method: 'GET' }
    );
  }

  async generateArticle(data: {
    post_id: string;
    user_id: string;
    regeneration_input?: string;
  }): Promise<any> {
    const response = await this.request<any>('/content-generation/article/generate', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response;
  }

  async updatePostCreativeCopy(data: {
    post_id: string;
    creative_copy: string;
  }): Promise<any> {
    const response = await this.request<any>(
      `/implementation/posts/${data.post_id}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ creative_copy: data.creative_copy }),
      }
    );
    return response;
  }

  async persistGeneratedAsset(data: {
    post_id: string;
    image_url: string;
    asset_s3_key?: string;
  }): Promise<any> {
    const response = await this.request<any>(
      `/implementation/posts/${data.post_id}`,
      {
        method: 'PATCH',
        body: JSON.stringify({ image_url: data.image_url, asset_s3_key: data.asset_s3_key || '' })
      }
    );
    return response;
  }

  async publishPost(data: {
    post_id: string;
    user_id: string;
  }): Promise<any> {
    return this.request<any>('/publish/post', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async generateCarousel(data: import('../types/carousel').CarouselGenerateRequest): Promise<
    | import('../types/carousel').CarouselGenerateResponse
    | import('../types/carousel').CarouselAlreadyGeneratedResponse
  > {
    return this.request<any>('/content-generation/carousel/generate', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Poll the background carousel generation job for a post. Returns
   * pending while the worker runs, done once the slide URLs are ready, or
   * error (credit charge already auto-refunded by the backend).
   */
  async getCarouselStatus(
    postId: string
  ): Promise<import('../types/carousel').CarouselGenerateResponse> {
    return this.request<import('../types/carousel').CarouselGenerateResponse>(
      `/content-generation/carousel/status/${encodeURIComponent(postId)}`,
      { method: 'GET' }
    );
  }

  // Dispatch + poll infographic generation. The backend now runs generation
  // in a background task: POST returns immediately with status="pending" and
  // the final S3 URL only arrives via GET /.../status/{post_id}. Consumers
  // should use useInfographicGeneration / infographicGenerationService.
  async generateInfographic(data: import('../types/infographic').InfographicGenerateRequest): Promise<
    | import('../types/infographic').InfographicGenerateResponse
    | import('../types/infographic').InfographicAlreadyGeneratedResponse
  > {
    return this.request<any>('/content-generation/infographic/generate', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Poll the background infographic generation job for a post. Returns
   * pending while the worker runs, done once the S3 URL is ready, or error
   * (credit charge already auto-refunded by the backend).
   */
  async getInfographicStatus(
    postId: string
  ): Promise<import('../types/infographic').InfographicGenerateResponse> {
    return this.request<import('../types/infographic').InfographicGenerateResponse>(
      `/content-generation/infographic/status/${encodeURIComponent(postId)}`,
      { method: 'GET' }
    );
  }

  // Dispatch + poll video generation. The backend runs generation in a
  // background task: POST returns immediately with status="pending" and
  // the final S3 URL only arrives via GET /.../status/{post_id}. Consumers
  // should use useVideoGeneration / videoGeneration.ts.
  async generateVideo(data: {
    user_id: string;
    post_id: string;
    caption?: string;
    duration?: string;
    resolution?: string;
    aspect_ratio?: string;
    generate_audio?: boolean;
    regeneration_instructions?: string;
    force_regenerate?: boolean;
  }): Promise<any> {
    return this.request<any>('/content-generation/video/generate', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /**
   * Poll the background video generation job for a post. Returns pending
   * while the worker runs, done once the video URL is ready, or error
   * (credit charge already auto-refunded by the backend).
   */
  async getVideoStatus(
    postId: string
  ): Promise<import('../types/video').VideoGenerateResponse> {
    return this.request<import('../types/video').VideoGenerateResponse>(
      `/content-generation/video/status/${encodeURIComponent(postId)}`,
      { method: 'GET' }
    );
  }

  // ==================== V2 Content Calendar Workspace API ====================

  /**
   * Fetch the full monthly calendar workspace (grid or list view).
   */
  async getImplementationWorkspace(
    calendarId: string,
    view: 'list' | 'grid' = 'list'
  ): Promise<any> {
    return this.request<any>(`/implementation/calendar/${encodeURIComponent(calendarId)}/workspace?view=${view}`, {
      method: 'GET'
    });
  }

  /**
   * Fetch full details of a single PostCard by ID.
   */
  async getPostCard(postId: string): Promise<any> {
    return this.request<any>(`/implementation/posts/${encodeURIComponent(postId)}`, {
      method: 'GET'
    });
  }

  /**
   * Update generic fields of a post card.
   */
  async updatePostCard(postId: string, fields: Record<string, any>): Promise<any> {
    return this.request<any>(`/implementation/posts/${encodeURIComponent(postId)}`, {
      method: 'PATCH',
      body: JSON.stringify(fields)
    });
  }

  /**
   * Delete a post from the content calendar.
   */
  async deletePost(postId: string): Promise<any> {
    return this.request<any>(`/implementation/posts/${encodeURIComponent(postId)}`, {
      method: 'DELETE'
    });
  }

  /**
   * Fetch available deletion/replacement slots and allowed formats for a calendar.
   */
  async getAvailableSlots(calendarId: string): Promise<any> {
    return this.request<any>(`/implementation/calendar/${encodeURIComponent(calendarId)}/available-slots`, {
      method: 'GET'
    });
  }

  /**
   * Add a new post to the calendar using an available deletion slot.
   */
  async addPost(
    calendarId: string,
    postData: {
      platform?: string;
      format?: string;
      scheduled_date?: string;
      theme?: string;
      caption?: string;
      creative_copy?: string;
      slot_id?: string;
    }
  ): Promise<any> {
    return this.request<any>(`/implementation/calendar/${encodeURIComponent(calendarId)}/posts`, {
      method: 'POST',
      body: JSON.stringify(postData)
    });
  }

  /**
   * Reschedule a post to a new date via drag-and-drop.
   */
  async reschedulePost(
    calendarId: string,
    postId: string,
    newDate: string
  ): Promise<any> {
    return this.request<any>(
      `/implementation/calendar/${encodeURIComponent(calendarId)}/posts/${encodeURIComponent(postId)}/reschedule`,
      {
        method: 'PATCH',
        body: JSON.stringify({ new_date: newDate })
      }
    );
  }

  /**
   * Edit post caption (saves manually).
   */
  async editCaption(postId: string, newCaption: string): Promise<any> {
    return this.request<any>(`/implementation/posts/${encodeURIComponent(postId)}/caption`, {
      method: 'PATCH',
      body: JSON.stringify({ new_caption: newCaption })
    });
  }

  /**
   * AI-regenerate post caption.
   */
  async regenerateCaption(postId: string, userPrompt: string, fieldType: string = 'caption'): Promise<any> {
    return this.request<any>(`/implementation/posts/${encodeURIComponent(postId)}/caption/regenerate`, {
      method: 'POST',
      body: JSON.stringify({ user_prompt: userPrompt, field_type: fieldType })
    });
  }

  /**
   * Edit post CTA (soft, discussion, nudges).
   */
  async editCTA(postId: string, cta: string, ctaType: string): Promise<any> {
    return this.request<any>(`/implementation/posts/${encodeURIComponent(postId)}/cta`, {
      method: 'PATCH',
      body: JSON.stringify({ cta, cta_type: ctaType })
    });
  }

  /**
   * Add or remove custom hashtags.
   */
  async editHashtags(
    postId: string,
    hashtag: string,
    action: 'add' | 'remove'
  ): Promise<any> {
    return this.request<any>(`/implementation/posts/${encodeURIComponent(postId)}/hashtags`, {
      method: 'PATCH',
      body: JSON.stringify({ hashtag, action })
    });
  }

  /**
   * Upload and link creative asset.
   */
  async uploadAsset(
    postId: string,
    assetType: string,
    file: File
  ): Promise<any> {
    const formData = new FormData();
    formData.append('asset_type', assetType);
    formData.append('file', file);

    return this.request<any>(`/implementation/posts/${encodeURIComponent(postId)}/assets/upload`, {
      method: 'POST',
      body: formData
    });
  }

  /**
   * Edit image asset with prompt.
   */
  async aiEditAsset(assetId: string, userPrompt: string): Promise<any> {
    return this.request<any>(`/implementation/assets/${encodeURIComponent(assetId)}/ai-edit`, {
      method: 'POST',
      body: JSON.stringify({ user_prompt: userPrompt })
    });
  }

  /**
   * Generate image variations.
   */
  async generateVariations(assetId: string): Promise<any> {
    return this.request<any>(`/implementation/assets/${encodeURIComponent(assetId)}/variations`, {
      method: 'POST'
    });
  }

  /**
   * Change post content format (trigger platform rules and strategy sync).
   */
  async changeFormat(postId: string, newFormat: string): Promise<any> {
    return this.request<any>(`/implementation/posts/${encodeURIComponent(postId)}/format`, {
      method: 'PATCH',
      body: JSON.stringify({ new_format: newFormat })
    });
  }

  /**
   * Export single post details.
   */
  async exportSinglePost(postId: string): Promise<any> {
    return this.request<any>(`/implementation/posts/${encodeURIComponent(postId)}/export`, {
      method: 'GET'
    });
  }

  async exportSinglePostZip(postId: string): Promise<Blob> {
    updateActivity(false);
    const token = localStorage.getItem('access_token');
    const headers: Record<string, string> = {
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
    const response = await fetch(`${this.baseUrl}/implementation/posts/${encodeURIComponent(postId)}/export-zip`, {
      method: 'GET',
      headers
    });
    if (!response.ok) {
      if (response.status === 401) {
        clearSession(false);
        throw new Error('Session expired');
      }
      let detail = 'Failed to export post zip';
      try {
        const body = await response.json();
        if (body?.detail) detail = body.detail;
      } catch {
        // Non-JSON error body — keep the generic message.
      }
      throw new Error(detail);
    }
    return response.blob();
  }

  async proxyAsset(url: string): Promise<Blob> {
    updateActivity(false);
    const token = localStorage.getItem('access_token');
    const headers: Record<string, string> = {
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
    const response = await fetch(`${this.baseUrl}/implementation/proxy-asset?url=${encodeURIComponent(url)}`, {
      method: 'GET',
      headers
    });
    if (!response.ok) {
      if (response.status === 401) {
        clearSession(false);
        throw new Error('Session expired');
      }
      throw new Error('Failed to proxy asset');
    }
    return response.blob();
  }

  async downloadImage(imageUrl: string): Promise<Blob> {
    updateActivity(false);
    const token = localStorage.getItem('access_token');
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
    const response = await fetch(`${this.baseUrl}/smm/implementation/download-image`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ image_url: imageUrl }),
    });
    if (!response.ok) {
      if (response.status === 401) {
        clearSession(false);
        throw new Error('Session expired');
      }
      throw new Error('Failed to download image');
    }
    return response.blob();
  }

  /**
   * Export full monthly calendar workspace as CSV.
   */
  async exportCalendarCSV(calendarId: string): Promise<Blob> {
    updateActivity(false);
    const token = localStorage.getItem('access_token');
    const headers: Record<string, string> = {
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
    const response = await fetch(`${this.baseUrl}/implementation/calendar/${encodeURIComponent(calendarId)}/export/csv`, {
      method: 'GET',
      headers
    });
    if (!response.ok) {
      if (response.status === 401) {
        clearSession(false);
        throw new Error('Session expired');
      }
      throw new Error('Failed to export CSV');
    }
    return response.blob();
  }

  // ==================== Optimization / Intelligence ====================
  async getMarketIntelligence(userId: string, refresh = false): Promise<any> {
    return this.request(`/optimization/market-intelligence/${userId}${refresh ? '?refresh=true' : ''}`, {
      method: 'GET'
    });
  }

  // Starts preparing every Social Media Manager page in the background
  // (missing / old reports), so they open instantly. Returns at once.
  async warmUpSmmPages(userId: string): Promise<any> {
    return this.request(`/smm/strategy/warmup/${userId}`, { method: 'POST' });
  }

  async getPerformanceIntelligence(userId: string, refresh = false): Promise<any> {
    return this.request(`/optimization/performance-intelligence/${userId}${refresh ? '?refresh=true' : ''}`, {
      method: 'GET'
    });
  }

  // ==================== Profile Connected Channels ====================
  async getProfileSocialChannels(businessId: string): Promise<any> {
    return this.request(`/profile/social-channels?business_id=${encodeURIComponent(businessId)}`, {
      method: 'GET'
    });
  }

  // Phase 2.5 — saveProfileSocialChannelUrl (POST /profile/social-channels/url)
  // removed: the endpoint had no callers and bypassed the ownership check on
  // PUT /profile/business-profile/{user_id}.

  async exchangeProfileMetaToken(business_id: string, code: string, platform: 'facebook' | 'instagram') {
    return this.request<any>('/profile/social-channels/meta/exchange', {
      method: 'POST',
      body: JSON.stringify({ business_id, code, platform })
    });
  }
  async exchangeProfileYoutubeToken(business_id: string, code: string) {
    return this.request<any>('/profile/social-channels/youtube/exchange', {
      method: 'POST',
      body: JSON.stringify({ business_id, code })
    });
  }
  /**
   * Phase 2.1 — persist the channel the user picked from the post-connect picker.
   * The backend re-checks ownership against channels.list(mine=true), so an id the
   * connected Google account does not own is rejected with 400.
   */
  async selectProfileYoutubeChannel(business_id: string, channel_id: string, channel_title?: string) {
    return this.request<any>('/profile/social-channels/youtube/select', {
      method: 'POST',
      body: JSON.stringify({ business_id, channel_id, channel_title })
    });
  }
  async exchangeProfileLinkedinToken(business_id: string, code: string) {
    return this.request<any>('/profile/social-channels/linkedin/exchange', {
      method: 'POST',
      body: JSON.stringify({ business_id, code })
    });
  }
  // ==================== Implementation Overview API ====================

  async getImplementationOverview(user_id: string) {
    return this.request<any>(`/seo/implementation/overview/${user_id}`, {
      method: 'GET'
    });
  }

  // ==================== On-Page Implementation: Image Optimizer API ====================

  async scanWebsiteImages(user_id: string, threshold_kb: number = 500, force_rescan: boolean = false) {
    return this.request<any>('/seo/on-page/image/scan', {
      method: 'POST',
      body: JSON.stringify({ user_id, threshold_kb, force_rescan })
    });
  }

  async getImageOptimizerStatus(user_id: string) {
    return this.request<any>(`/seo/on-page/image/status/${user_id}`, {
      method: 'GET'
    });
  }

  async markImageOptimizerComplete(user_id: string) {
    return this.request<any>('/seo/on-page/image/mark-complete', {
      method: 'POST',
      body: JSON.stringify({ user_id })
    });
  }

  async optimizeImageBatch(user_id: string, files: File[], is_hero: boolean = false) {
    const formData = new FormData();
    formData.append('user_id', user_id);
    formData.append('is_hero', is_hero.toString());
    files.forEach(file => {
      formData.append('files', file);
    });

    return this.request<any>('/seo/on-page/image/optimize', {
      method: 'POST',
      body: formData
    });
  }

  // ==================== On-Page Implementation: Meta Title Generator API ====================

  async auditWebsiteMeta(user_id: string, max_pages: number = 10, force_rescan: boolean = false) {
    return this.request<any>('/seo/on-page/meta/audit', {
      method: 'POST',
      body: JSON.stringify({ user_id, max_pages, force_rescan })
    });
  }

  async markMetaTitleGeneratorComplete(user_id: string, audit_data: any) {
    return this.request<any>('/seo/on-page/meta/mark-complete', {
      method: 'POST',
      body: JSON.stringify({ user_id, audit_data })
    });
  }

  async getMetaTitleGeneratorStatus(user_id: string) {
    return this.request<any>(`/seo/on-page/meta/status/${user_id}`, {
      method: 'GET'
    });
  }

  async generateMetaTags(data: { user_id: string; page_url: string; page_type: string; business_category?: string }) {
    return this.request<any>('/seo/on-page/meta/generate', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async regenerateMetaTag(data: { user_id: string; page_url: string; tag_name: string; current_value: string; current_title?: string }) {
    return this.request<any>('/seo/on-page/meta/regenerate', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  // ==================== Image Submission API ====================

  async createImageSubmissionJob(business_id: string, mode: 'new_website' | 'existing_website') {
    return this.request<any>('/off-page/image-submission/jobs', {
      method: 'POST',
      body: JSON.stringify({ business_id, mode })
    });
  }

  async saveImageSubmissionInputs(job_id: string, data: any) {
    return this.request<any>(`/off-page/image-submission/jobs/${job_id}/inputs`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async triggerAIImageGeneration(job_id: string, data: any) {
    return this.request<any>(`/off-page/image-submission/jobs/${job_id}/generate-images`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async uploadImageSubmissionFiles(job_id: string, files: File[]) {
    const formData = new FormData();
    files.forEach(file => {
      formData.append('files', file);
    });
    return this.request<any>(`/off-page/image-submission/jobs/${job_id}/upload-images`, {
      method: 'POST',
      body: formData
    });
  }

  async getImageSubmissionAssets(job_id: string) {
    return this.request<any>(`/off-page/image-submission/jobs/${job_id}/assets`, {
      method: 'GET'
    });
  }


  async trackImageSubmission(assetId: string, platform: string, platformUrl: string) {
    return this.request<any>(`/off-page/image-submission/assets/${encodeURIComponent(assetId)}/track`, {
      method: 'POST',
      body: JSON.stringify({ platform, platform_url: platformUrl })
    });
  }

  async completeImageSubmission(assetId: string, payload: { platforms: string[] }) {
    return this.request<any>(`/off-page/image-submission/assets/${encodeURIComponent(assetId)}/complete`, {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }


  async getImageSubmissionPlatforms() {
    return this.request<any[]>('/off-page/image-submission/platforms', {
      method: 'GET'
    });
  }

  async getImageSubmissionLibrary() {
    return this.request<any[]>('/off-page/image-submission/library', {
      method: 'GET'
    });
  }

  // ==================== Blog / FAQ API ====================

  async getBlogAuthors(userId: string) {
    return this.request<any>(`/blog/authors/${userId}`, { method: 'GET' });
  }

  async discoverBlogTopics(userId: string, force?: boolean) {
    return this.request<any>(`/blog/discover-topics`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId, force_refresh: !!force })
    });
  }

  /** Start async blog generation. Returns { job_id, status: "QUEUED", ... } */
  async generateBlog(data: { user_id: string; topic: string; author_id?: number | null; manual_author_name?: string; differentiation: string }) {
    return this.request<any>(`/blog/generate`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  /** Poll this every 5s while status is QUEUED/PROCESSING. Returns job with progress, current_step, blog_id. */
  async getBlogJob(jobId: string, userId: string) {
    return this.request<any>(`/blog/jobs/${jobId}?user_id=${userId}`, { method: 'GET' });
  }

  /** Called on page load — resumes polling if a job was started before page refresh. */
  async getActiveBlogJob(userId: string) {
    return this.request<any>(`/blog/jobs/active/${userId}`, { method: 'GET' });
  }

  /** Request cancellation of a running job. */
  async cancelBlogJob(jobId: string, userId: string) {
    return this.request<any>(`/blog/jobs/${jobId}/cancel?user_id=${userId}`, { method: 'POST' });
  }

  /** Fetch a single completed blog by ID — avoids reloading full history. */
  async getSingleBlog(blogId: number, userId: string) {
    return this.request<any>(`/blog/${blogId}?user_id=${userId}`, { method: 'GET' });
  }

  async publishBlog(blogId: number, userId: string) {
    return this.request<any>(`/blog/${blogId}/publish?user_id=${userId}`, {
      method: 'PATCH'
    });
  }

  async updateBlogContent(blogId: number, userId: string, content: string) {
    return this.request<any>(`/blog/${blogId}/content?user_id=${userId}`, {
      method: 'PATCH',
      body: JSON.stringify({ content })
    });
  }

  /** Re-generate only the blog cover poster (brand logo + AI image). Does not charge credits. */
  async regenerateBlogImage(blogId: number, userId: string) {
    return this.request<any>(`/blog/${blogId}/regenerate-image?user_id=${userId}`, {
      method: 'POST',
    });
  }

  // ── New Registration Flow: Company Verification Gate ─────────────────────

  /**
   * Stage 1: Check if a company name is registered and what its onboarding status is.
   * Returns: { status: 'not_registered' | 'already_onboarded' | 'pending_onboarding', masked_email? }
   */
  async verifyCompanyIdentity(company_name: string) {
    return this.request<{
      status: 'not_registered' | 'already_onboarded' | 'pending_onboarding';
      message: string;
      masked_email?: string;
    }>(`/onboarding/verify-company-identity?company_name=${encodeURIComponent(company_name)}`, {
      method: 'POST'
    });
  }

  /**
   * Stage 1 verification: Validate the secret code for a given company name.
   * Returns: { status: 'verified', message: string }
   */
  async verifySecretCode(company_name: string, secret_code: string) {
    return this.request<{ status: string; message: string; full_name?: string; email?: string }>(
      `/onboarding/verify-secret-code?company_name=${encodeURIComponent(company_name)}&secret_code=${encodeURIComponent(secret_code)}`,
      { method: 'POST' }
    );
  }


  // ── Brand Equity ──────────────────────────────────────────

  async getBrandEquityReport(userId: string) {
    return this.get<any>(`/brand-equity/report?user_id=${userId}`);
  }

  async calculateBrandEquity(userId: string) {
    return this.post<any>(`/brand-equity/calculate?user_id=${userId}`);
  }

  async downloadBrandEquityPdf(userId: string) {
    return this.getBlob(`/brand-equity/pdf?user_id=${userId}`);
  }

  async regenerateBrandEquityNarrative(userId: string) {
    return this.post<any>(`/brand-equity/narrative?user_id=${userId}`);
  }

  // ==================== COMPETITOR BENCHMARK ====================

  /**
   * Governance: Get Competitor Benchmark Data
   * Returns authority cards, keyword gap table, and opportunity scores.
   */
  async getCompetitorBenchmark(userId: string) {
    return this.request<any>(`/governance/competitor-benchmark/${userId}`, {
      method: 'GET',
    });
  }

  /**
   * Governance: Target / Un-target a Benchmark Keyword
   * Flags a locked keyword as Competitive Opportunity Priority.
   */
  async targetCompetitorKeyword(
    userId: string,
    keywordId: number,
    isTargeted: boolean
  ) {
    return this.request<any>('/governance/competitor-benchmark/target-keyword', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        keyword_id: keywordId,
        is_targeted: isTargeted,
      }),
    });
  }

  /**
   * Governance: Force-Refresh Competitor Benchmark
   * Clears 30-day cache and re-fetches fresh DataForSEO data.
   */
  async refreshCompetitorBenchmark(userId: string) {
    return this.request<any>(`/governance/competitor-benchmark/refresh/${userId}`, {
      method: 'POST',
    });
  }

}



// Export singleton instance
export const apiService = new ApiService()
export default apiService

